import { apiClient } from '@/shared/lib/apiClient';

export type RevenuePeriod =
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'yearly'
  | 'all'
  | 'custom';

export type RevenueDashboardResponse = {
  summary: {
    free_trial: number;
    paid_active: number;
    pending_payment: number;
    past_due: number;
    cancelled: number;
    expiring_in_7_days: number;
    expiring_in_30_days: number;
  };
  payments: {
    instapay: { count: number; total_minor: number };
    myfatoorah: { count: number; total_minor: number };
  };
  monthly_trend: Array<{ month: string; count: number }>;
  period: RevenuePeriod;
};

export type ApiSuccessResponse<T> = { success: true; data: T };

export type DateRangeFilter = { fromDate?: string; toDate?: string };

export async function fetchRevenueDashboard(
  period: RevenuePeriod = 'all',
  productSlug?: string,
  range?: DateRangeFilter,
): Promise<RevenueDashboardResponse> {
  const params: Record<string, string> = { period };
  if (productSlug) params.productSlug = productSlug;
  if (range?.fromDate) params.fromDate = range.fromDate;
  if (range?.toDate) params.toDate = range.toDate;

  const res = await apiClient.get<ApiSuccessResponse<RevenueDashboardResponse>>(
    '/api/v1/admin/revenue-dashboard',
    { params },
  );
  return res.data;
}

export type RevenueDetailCategory =
  | 'free_trial'
  | 'paid_active'
  | 'pending_payment'
  | 'past_due'
  | 'cancelled'
  | 'expiring_in_7_days'
  | 'expiring_in_30_days';

export type RevenueDetailItem = {
  id: string;
  tenant_name: string;
  product_name: string;
  product_slug: string;
  plan_name: string;
  status: string;
  email: string | null;
  phone: string | null;
};

export type RevenueDetailsResponse = {
  category: string;
  items: RevenueDetailItem[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
};

export async function fetchRevenueDashboardDetails(
  category: RevenueDetailCategory,
  period: RevenuePeriod = 'all',
  page = 1,
  pageSize = 25,
  range?: DateRangeFilter,
): Promise<RevenueDetailsResponse> {
  const params: Record<string, string> = {
    category,
    period,
    page: String(page),
    pageSize: String(pageSize),
  };
  if (range?.fromDate) params.fromDate = range.fromDate;
  if (range?.toDate) params.toDate = range.toDate;

  const res = await apiClient.get<ApiSuccessResponse<RevenueDetailsResponse>>(
    '/api/v1/admin/revenue-dashboard/details',
    { params },
  );
  return res.data;
}

export type ProductComparisonItem = {
  product_slug: string;
  product_name: string;
  product_name_ar: string;
  subscriber_count: number;
  active_count: number;
  revenue_minor: number;
};

export async function fetchProductComparison(
  period: RevenuePeriod = 'all',
  range?: DateRangeFilter,
): Promise<{ products: ProductComparisonItem[] }> {
  const params: Record<string, string> = { period };
  if (range?.fromDate) params.fromDate = range.fromDate;
  if (range?.toDate) params.toDate = range.toDate;

  const res = await apiClient.get<ApiSuccessResponse<{ products: ProductComparisonItem[] }>>(
    '/api/v1/admin/revenue-dashboard/product-comparison',
    { params },
  );
  return res.data;
}

export type PlanDistributionItem = {
  tier_slug: string;
  tier_name: string;
  tier_name_ar: string;
  count: number;
};

export async function fetchPlanDistribution(
  period: RevenuePeriod = 'all',
  range?: DateRangeFilter,
): Promise<{ tiers: PlanDistributionItem[] }> {
  const params: Record<string, string> = { period };
  if (range?.fromDate) params.fromDate = range.fromDate;
  if (range?.toDate) params.toDate = range.toDate;

  const res = await apiClient.get<ApiSuccessResponse<{ tiers: PlanDistributionItem[] }>>(
    '/api/v1/admin/revenue-dashboard/plan-distribution',
    { params },
  );
  return res.data;
}

export type PeriodComparisonResponse = {
  period: string;
  current: { new_subscriptions: number; revenue_minor: number };
  previous: { new_subscriptions: number; revenue_minor: number };
  change_pct: { new_subscriptions: number | null; revenue: number | null };
};

export async function fetchPeriodComparison(
  period: RevenuePeriod = 'monthly',
): Promise<PeriodComparisonResponse> {
  const res = await apiClient.get<ApiSuccessResponse<PeriodComparisonResponse>>(
    '/api/v1/admin/revenue-dashboard/period-comparison',
    { params: { period } },
  );
  return res.data;
}

export type ConversionRateResponse = {
  total_trialed: number;
  converted_to_paid: number;
  conversion_rate_pct: number;
};

export async function fetchConversionRate(
  period: RevenuePeriod = 'all',
  range?: DateRangeFilter,
): Promise<ConversionRateResponse> {
  const params: Record<string, string> = { period };
  if (range?.fromDate) params.fromDate = range.fromDate;
  if (range?.toDate) params.toDate = range.toDate;

  const res = await apiClient.get<ApiSuccessResponse<ConversionRateResponse>>(
    '/api/v1/admin/revenue-dashboard/conversion-rate',
    { params },
  );
  return res.data;
}
